import React from "react";
import { css, cx } from "@emotion/css";
import type { ResolvedQuestionnaire } from "../api";
import { serializeResolvedQuestionnaire } from "../serializeResolved";
import { buildRows, collapseRows, Row } from "../diffRows";

const CONTEXT = 3;

const RowView: React.VFC<{ row: Row }> = ({ row }) => {
  const leftHighlight = row.kind === "removed" || row.kind === "modified";
  const rightHighlight = row.kind === "added" || row.kind === "modified";
  return (
    <div className={styles.row}>
      <div className={cx(styles.side, leftHighlight && styles.removed)}>
        <div className={styles.lineNo}>{row.left?.lineNo ?? ""}</div>
        <div className={styles.lineText}>{row.left?.text ?? ""}</div>
      </div>
      <div className={cx(styles.side, rightHighlight && styles.added)}>
        <div className={styles.lineNo}>{row.right?.lineNo ?? ""}</div>
        <div className={styles.lineText}>{row.right?.text ?? ""}</div>
      </div>
    </div>
  );
};

const QuestionnaireDiff: React.VFC<{
  previous: ResolvedQuestionnaire;
  next: ResolvedQuestionnaire;
  previousLabel?: string;
  nextLabel?: string;
}> = ({ previous, next, previousLabel = "Previous", nextLabel = "Next" }) => {
  const oldStr = React.useMemo(() => serializeResolvedQuestionnaire(previous), [previous]);
  const newStr = React.useMemo(() => serializeResolvedQuestionnaire(next), [next]);
  const rows = React.useMemo(() => buildRows(oldStr, newStr), [oldStr, newStr]);
  const display = React.useMemo(() => collapseRows(rows, CONTEXT), [rows]);
  const hasChanges = rows.some(r => r.kind !== "unchanged");

  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  React.useEffect(() => {
    setExpanded(new Set());
  }, [oldStr, newStr]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerCell}>{previousLabel}</div>
        <div className={styles.headerCell}>{nextLabel}</div>
      </div>
      {!hasChanges && <div className={styles.noChanges}>No textual differences.</div>}
      <div>
        {display.map(item =>
          item.kind === "row" ? (
            <RowView key={`row-${item.index}`} row={item.row} />
          ) : expanded.has(item.key) ? (
            <React.Fragment key={`exp-${item.key}`}>
              {item.hidden.map((r, j) => (
                <RowView key={`hidden-${item.key}-${j}`} row={r} />
              ))}
            </React.Fragment>
          ) : (
            <button
              key={`col-${item.key}`}
              className={styles.collapsed}
              onClick={() => setExpanded(s => new Set(s).add(item.key))}
            >
              Show {item.hidden.length} unchanged {item.hidden.length === 1 ? "line" : "lines"}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default QuestionnaireDiff;

const styles = {
  container: css`
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    width: 100%;
  `,
  header: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    position: sticky;
    top: 0;
    z-index: 1;
  `,
  headerCell: css`
    padding: 6px 12px;
    font-weight: 600;
    border-right: 1px solid #e0e0e0;
    &:last-child {
      border-right: none;
    }
  `,
  noChanges: css`
    padding: 12px;
    color: #666;
    font-style: italic;
  `,
  row: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
  `,
  side: css`
    display: grid;
    grid-template-columns: 50px 1fr;
    border-right: 1px solid #f0f0f0;
    border-bottom: 1px solid #f5f5f5;
    min-height: 1.4em;
    &:last-child {
      border-right: none;
    }
  `,
  lineNo: css`
    text-align: right;
    color: #999;
    padding: 1px 6px;
    user-select: none;
    background: #fafafa;
    border-right: 1px solid #f0f0f0;
  `,
  lineText: css`
    padding: 1px 8px;
    white-space: pre-wrap;
    word-break: break-word;
  `,
  removed: css`
    background: #ffeef0;
    & > div:first-child {
      background: #fdd9dd;
    }
  `,
  added: css`
    background: #e6ffed;
    & > div:first-child {
      background: #cdffd8;
    }
  `,
  collapsed: css`
    width: 100%;
    padding: 6px 12px;
    background: #f0f3f6;
    border: none;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    color: #0366d6;
    font-family: inherit;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
    &:hover {
      background: #e6ecf2;
    }
  `,
};
